import { LoadingValue } from "@/components/LoadingContent";
import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm, useFormState, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { Alert, Button, Divider, PasswordInput, Stack, Text, TextInput } from "@mantine/core";

import { CenteredCard } from "@/components/CenteredCard";
import { signIn, emailOtp } from "@/lib/authClient";
import { useAuthMethods } from "@/features/auth/hooks";
import { SocialButtons } from "@/features/auth/components/SocialButtons";
import { authSearchSchema, safeCallbackUrl, safeNextUrl } from "@/features/auth/redirect";
import { useAuthUiStore } from "@/features/auth/store";
import { AuthMethodsStatus } from "@/features/auth/components/AuthMethodsStatus";

const signInSchema = z.object({
  email: z.email("Enter a valid email address."),
  password: z.string().min(1, "Enter your password."),
});

type SignInValues = z.infer<typeof signInSchema>;

function SignInPage() {
  const { next, email } = Route.useSearch();
  const navigate = useNavigate();
  const passwordSaved = useAuthUiStore((state) => state.passwordSaved);
  const { emailPasswordEnabled, registrationEnabled, socialProviders, isLoading, error: metadataError, retry, methods } = useAuthMethods();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    getValues,
  } = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: email ?? "", password: "" },
  });
  const { errors, isSubmitting } = useFormState({ control });
  const currentEmail = useWatch({ control, name: "email" });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    if (!emailPasswordEnabled) return;

    const { error } = await signIn.email({
      email: values.email,
      password: values.password,
    });

    if (!error) {
      useAuthUiStore.getState().setPasswordSaved(false);
      window.location.assign(safeNextUrl(next));
      return;
    }

    // Sign-in itself does not send a verification code. Only this specific
    // error starts verification; other account denials remain actionable errors.
    if (error.code === "EMAIL_NOT_VERIFIED") {
      const sent = await emailOtp.sendVerificationOtp({
        email: values.email,
        type: "email-verification",
      });
      if (sent.error) {
        setFormError(sent.error.message ?? "Could not send a verification code. Please try again.");
        return;
      }
      void navigate({
        to: "/auth/verify-email",
        search: { email: values.email, next },
      });
      return;
    }

    setFormError(error.message ?? "Could not sign you in. Check your details and try again.");
  });

  return (
    <CenteredCard
      title="Sign in"
      description="Sign in to your TripBears account to continue."
      footer={
        isLoading ? <LoadingValue loading width={240}>{null}</LoadingValue> : registrationEnabled ? <Text size="sm" c="dimmed">
          Don't have an account?{" "}
          <Text
            component="a"
            href="#"
            size="sm"
            c="var(--mantine-color-primary-text)"
            onClick={(event) => {
              event.preventDefault();
              void navigate({ to: "/auth/sign-up", search: { next } });
            }}
          >
            Create one
          </Text>
        </Text> : <Text size="sm" c="dimmed">New-account registration is currently unavailable. Existing accounts can sign in using an enabled method.</Text>
      }
    >
      <Stack gap="md">
        {passwordSaved ? <Alert color="green" title="Password saved" role="status">Your password was saved. Please sign in again with your email and new password.</Alert> : null}
        <AuthMethodsStatus isLoading={isLoading} error={metadataError} retry={retry} noMethods={methods.length === 0} />
        <SocialButtons callbackURL={safeCallbackUrl(next)} />

        {emailPasswordEnabled ? (
          <>
            {socialProviders.length > 0 ? <Divider label="or" labelPosition="center" /> : null}

            <form onSubmit={(event) => void onSubmit(event)} noValidate>
              <Stack gap="md">
                <TextInput
                  label="Email"
                  type="email"
                  autoComplete="email"
                  error={errors.email?.message}
                  {...register("email")}
                />
                <PasswordInput
                  label="Password"
                  autoComplete="current-password"
                  error={errors.password?.message}
                  {...register("password")}
                />

                {formError ? (
                  <Alert color="red" variant="light">
                    {formError}
                  </Alert>
                ) : null}

                <Button type="submit" size="md" fullWidth loading={isSubmitting}>
                  Sign in
                </Button>

                <Text
                  component="a"
                  href={`/auth/forgot-password?${new URLSearchParams({ ...(next ? { next } : {}), email: currentEmail })}`}
                  size="sm"
                  c="var(--mantine-color-primary-text)"
                  ta="center"
                >
                  Forgot password?
                </Text>
                <Text
                  component="a"
                  href="#"
                  size="sm"
                  c="dimmed"
                  ta="center"
                  onClick={(event) => {
                    event.preventDefault();
                    void navigate({
                      to: "/auth/verify-email",
                      search: { email: getValues("email") || undefined, next },
                    });
                  }}
                >
                  I have a verification code
                </Text>
              </Stack>
            </form>
          </>
        ) : null}
      </Stack>
    </CenteredCard>
  );
}

export const Route = createFileRoute("/auth/sign-in")({
  validateSearch: authSearchSchema,
  component: SignInPage,
});
